import { Button, Flex, Table, Text } from "@chakra-ui/react"
import { useMemo, useState } from "react"
import { LuArrowDown, LuArrowUp, LuArrowUpDown } from "react-icons/lu"

import type { TeacherQuizAnalyticsRow } from "@/types/dashboard"

type SortKey =
  | "quiz_name"
  | "started_attempts"
  | "completed_attempts"
  | "completion_rate"
  | "avg_score_percentage"
type SortDirection = "asc" | "desc"

interface QuizAnalyticsTableProps {
  rows: TeacherQuizAnalyticsRow[]
}

function SortIndicator({
  active,
  direction,
}: {
  active: boolean
  direction: SortDirection
}) {
  if (!active) return <LuArrowUpDown size={14} aria-hidden="true" />
  return direction === "asc" ? (
    <LuArrowUp size={14} aria-hidden="true" />
  ) : (
    <LuArrowDown size={14} aria-hidden="true" />
  )
}

function SortableHeader({
  label,
  sortKey,
  activeKey,
  direction,
  onClick,
}: {
  label: string
  sortKey: SortKey
  activeKey: SortKey
  direction: SortDirection
  onClick: (key: SortKey) => void
}) {
  return (
    <Button
      variant="ghost"
      size="xs"
      px={0}
      color="inherit"
      aria-label={`Sort by ${label}`}
      onClick={() => onClick(sortKey)}
    >
      <Flex align="center" gap={1}>
        <Text as="span" fontWeight="semibold">
          {label}
        </Text>
        <SortIndicator active={activeKey === sortKey} direction={direction} />
      </Flex>
    </Button>
  )
}

export function QuizAnalyticsTable({ rows }: QuizAnalyticsTableProps) {
  const [sortState, setSortState] = useState<{
    key: SortKey
    direction: SortDirection
  }>({ key: "started_attempts", direction: "desc" })

  const sortedRows = useMemo(() => {
    return rows
      .map((row, sourceIndex) => ({ row, sourceIndex }))
      .sort((left, right) => {
        const leftValue = left.row[sortState.key]
        const rightValue = right.row[sortState.key]

        if (leftValue === null && rightValue === null) {
          return left.sourceIndex - right.sourceIndex
        }
        if (leftValue === null) return 1
        if (rightValue === null) return -1

        const comparison =
          typeof leftValue === "string" && typeof rightValue === "string"
            ? leftValue.localeCompare(rightValue)
            : Number(leftValue) - Number(rightValue)

        return sortState.direction === "asc" ? comparison : -comparison
      })
  }, [rows, sortState])

  function handleSort(key: SortKey) {
    setSortState((current) => {
      if (current.key === key) {
        return {
          key,
          direction: current.direction === "asc" ? "desc" : "asc",
        }
      }
      return {
        key,
        direction: key === "quiz_name" ? "asc" : "desc",
      }
    })
  }

  return (
    <Table.Root size="sm" variant="outline" data-testid="quiz-analytics-table">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader>
            <SortableHeader
              label="Quiz"
              sortKey="quiz_name"
              activeKey={sortState.key}
              direction={sortState.direction}
              onClick={handleSort}
            />
          </Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right">
            <SortableHeader
              label="Started"
              sortKey="started_attempts"
              activeKey={sortState.key}
              direction={sortState.direction}
              onClick={handleSort}
            />
          </Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right">
            <SortableHeader
              label="Completed"
              sortKey="completed_attempts"
              activeKey={sortState.key}
              direction={sortState.direction}
              onClick={handleSort}
            />
          </Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right">
            <SortableHeader
              label="Completion rate"
              sortKey="completion_rate"
              activeKey={sortState.key}
              direction={sortState.direction}
              onClick={handleSort}
            />
          </Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right">
            <SortableHeader
              label="Avg score"
              sortKey="avg_score_percentage"
              activeKey={sortState.key}
              direction={sortState.direction}
              onClick={handleSort}
            />
          </Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {sortedRows.map(({ row }) => (
          <Table.Row
            key={row.quiz_id}
            data-testid={`quiz-analytics-row-${row.quiz_id}`}
          >
            <Table.Cell fontWeight="medium">{row.quiz_name}</Table.Cell>
            <Table.Cell textAlign="right">{row.started_attempts}</Table.Cell>
            <Table.Cell textAlign="right">{row.completed_attempts}</Table.Cell>
            <Table.Cell textAlign="right">
              {row.completion_rate.toFixed(1)}%
            </Table.Cell>
            <Table.Cell textAlign="right">
              {row.avg_score_percentage === null
                ? "N/A"
                : `${row.avg_score_percentage.toFixed(1)}%`}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
}
